import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { ReactNode } from 'react';

type AccordionItemProps = {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
};

function AccordionItem({
  title,
  children,
  defaultOpen = false
}: AccordionItemProps) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div className='rounded-lg bg-gray-100'>
          <Disclosure.Button className='flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500/50'>
            <span>{title}</span>
            {open ? (
              <ChevronUpIcon className='h-5 w-5 text-gray-500' />
            ) : (
              <ChevronDownIcon className='h-5 w-5 text-gray-500' />
            )}
          </Disclosure.Button>
          <Disclosure.Panel className='px-4 pb-4 pt-2'>
            {children}
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}

type AccordionCategoryProps = {
  id?: string;
  title: string;
  description: string;
  children: ReactNode;
  defaultOpen?: boolean;
};

function AccordionCategory({
  id,
  title,
  description,
  children,
  defaultOpen = false
}: AccordionCategoryProps) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div id={id} className='rounded-xl border border-gray-200 bg-gray-100'>
          <Disclosure.Button
            className={clsx(
              'flex w-full items-center justify-between px-5 py-4 text-left hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500/50',
              open ? 'rounded-t-xl' : 'rounded-xl'
            )}
          >
            <div>
              <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
              <p className='text-sm text-gray-500'>{description}</p>
            </div>
            {open ? (
              <ChevronUpIcon className='h-6 w-6 text-gray-500' />
            ) : (
              <ChevronDownIcon className='h-6 w-6 text-gray-500' />
            )}
          </Disclosure.Button>
          <Disclosure.Panel className='flex flex-col gap-2 rounded-b-xl border-t border-gray-200 bg-white px-4 py-3'>
            {children}
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}

export { AccordionCategory, AccordionItem };
